/**
 * A completed-trip review is valuable marketplace feedback, but it is never a
 * reason to trap somebody under a modal. Keep the "not now" decision local,
 * small, and independent from the authoritative review state in Postgres.
 */
export const REVIEW_NUDGE_DEFER_MS = 24 * 60 * 60 * 1000

export interface ReviewNudgeDeferral {
  rentalId: string
  deferredUntil: number
}

export function createReviewNudgeDeferral(
  rentalId: string,
  now: number = Date.now(),
): ReviewNudgeDeferral {
  return {
    rentalId,
    deferredUntil: now + REVIEW_NUDGE_DEFER_MS,
  }
}

/** Reject malformed and expired disk state rather than letting it mute a prompt forever. */
export function parseReviewNudgeDeferral(
  raw: string | null | undefined,
  now: number = Date.now(),
): ReviewNudgeDeferral | null {
  if (!raw) return null

  try {
    const value = JSON.parse(raw) as Partial<ReviewNudgeDeferral>
    if (
      typeof value.rentalId !== 'string' ||
      value.rentalId.trim().length === 0 ||
      typeof value.deferredUntil !== 'number' ||
      !Number.isFinite(value.deferredUntil) ||
      value.deferredUntil <= now
    ) {
      return null
    }

    return { rentalId: value.rentalId, deferredUntil: value.deferredUntil }
  } catch {
    return null
  }
}

export function isReviewNudgeDeferred(
  rentalId: string | null | undefined,
  deferral: ReviewNudgeDeferral | null | undefined,
  now: number = Date.now(),
): boolean {
  return Boolean(
    rentalId && deferral && deferral.rentalId === rentalId && deferral.deferredUntil > now,
  )
}
